<template>
    <div class="assign-owner-process-modal">
        <ProcessModal
            data-qa="assign-owner-process-modal-process"
            :is-open="isOpen"
            :title="$t('funnel.edit.process.assign.owner.title')"
            :summary-text="$t('funnel.edit.process.assign.owner.summary')"
            :show-save="showSave"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #processParameters>
                <div class="vertical-container">
                    <DsRadio
                        v-model="radioValue"
                        val="assignToOwner"
                        class="assign-to-owner-choice"
                        :label="$t('funnel.edit.process.assign.owner.option.assign.owner')"
                        @input="changeRadioOption"
                    />
                    <DsRadio
                        v-if="!isSpaApp"
                        v-model="radioValue"
                        val="roundRobin"
                        class="round-robin-choice"
                        :label="$t('funnel.edit.process.assign.owner.option.using.round.robin')"
                        @input="changeRadioOption"
                    />

                    <DsMultiselect
                        v-model="userOrRoundRobinSelected"
                        label-prop="value"
                        value-prop="id"
                        required
                        :placeholder="userOrRoundRobinPlaceholder"
                        :options="generalUserOrRROptions"
                        :submitted="submitted"
                        class="users-round-robin-lists"
                    >
                        <template #help>
                            <small v-if="showErrorMessage">
                                {{ $t('funnel.edit.process.assign.owner.required.selected.user') }}
                            </small>
                        </template>
                    </DsMultiselect>

                    <DsCheckbox
                        v-model="ignoreChecked"
                        class="checkbox-ignore-assigned-user"
                        :label="$t('funnel.edit.process.assign.owner.checkbox.description')"
                    />
                </div>
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import ProcessModal from '../processModal/ProcessModal.vue';
import { loadInitialConfig } from './assign.owner.api';

export const ASSIGN_OWNER_TYPE = {
    assignToOwner: 'assignToOwner',
    roundRobin: 'roundRobin',
};


export default {
    ASSIGN_OWNER_TYPE,
    components: {
        ProcessModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    userId: 0,
                    roundRobinId: 0,
                    skipAssigned: '0',
                },
            }),
        },
    },
    emits: ['update-cell-value', 'close-modal'],
    data() {
        return {
            userOrRoundRobinSelected: null,
            generalUserOrRROptions: [],
            usersListOptions: [],
            roundRobinListOptions: [],
            showSave: true,
            radioValue: '',
            ignoreChecked: this.cell?.value?.skipAssigned === '1',
            showErrorMessage: false,
            submitted: false,
        };
    },

    computed: {
        isUserSelectEnabled() {
            return this.radioValue === this.$options.ASSIGN_OWNER_TYPE.assignToOwner;
        },
        userOrRoundRobinPlaceholder() {
            let placeholder = '';

            if (this.isUserSelectEnabled) {
                placeholder = this.$t('funnel.edit.process.assign.owner.select.user.placeholder');
            } else {
                placeholder = this.$t('funnel.edit.process.assign.owner.select.round.robin.placeholder');
            }

            return placeholder;
        },
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
    },

    async mounted() {
        await this.loadAllUsers();
    },

    methods: {
        closeModal() {
            this.$emit('close-modal');
        },

        async updateConfig() {
            this.submitted = true;
            const cell = { ...this.cell };

            cell.value = cell.value || {};
            cell.value.userId = null;
            cell.value.roundRobinId = null;
            cell.value.skipAssigned = this.ignoreChecked ? '1' : '0';

            try {
                if (!this.isEmptyObject(this.userOrRoundRobinSelected)) {
                    if (this.isUserSelectEnabled) {
                        cell.value.userId = this.userOrRoundRobinSelected.id;
                        cell.value.skipAssigned = this.ignoreChecked ? '1' : '0';
                    } else {
                        cell.value.roundRobinId = this.userOrRoundRobinSelected.id;
                        cell.value.skipAssigned = this.ignoreChecked ? '1' : '0';
                    }

                    this.$emit('update-cell-value', { cell });
                } else {
                    this.showErrorMessage = true;

                    return;
                }
            } catch (error) {
                this.$error({
                    message: error,
                });
            }
            this.submitted = false;
        },
        isEmptyObject(obj) {
            if (obj === null) {
                return true;
            }

            return Object.keys(obj).length === 0;
        },
        changeRadioOption() {
            this.userOrRoundRobinSelected = null;
            this.submitted = false;
            this.showErrorMessage = false;

            if (this.isUserSelectEnabled) {
                this.generalUserOrRROptions = this.usersListOptions;
            } else {
                this.generalUserOrRROptions = this.roundRobinListOptions;
            }
        },
        async loadAllUsers() {
            try {
                const tempUserId = parseInt(this.cell?.value?.userId ?? 0, 10);
                const tempRoundRobinId = parseInt(this.cell?.value?.roundRobinId ?? 0, 10);

                this.ignoreChecked = this.cell?.value?.skipAssigned === '1';

                const { userMap, roundRobinMap } = await loadInitialConfig();

                this.usersListOptions = Object.entries(userMap || []).map(([id, value]) => ({
                    id: parseInt(id, 10),
                    value,
                }));
                this.roundRobinListOptions = Object.entries(roundRobinMap || []).map(([id, value]) => ({
                    id: parseInt(id, 10),
                    value,
                }));

                if (!tempUserId && !tempRoundRobinId) {
                    this.radioValue = this.$options.ASSIGN_OWNER_TYPE.assignToOwner;
                    this.generalUserOrRROptions = this.usersListOptions;
                    this.userOrRoundRobinSelected = this.generalUserOrRROptions.find((u) => u.id === tempUserId);
                } else if (tempUserId) {
                    this.radioValue = this.$options.ASSIGN_OWNER_TYPE.assignToOwner;
                    this.generalUserOrRROptions = this.usersListOptions;
                    this.userOrRoundRobinSelected = this.generalUserOrRROptions.find((u) => u.id === tempUserId);
                } else if (tempRoundRobinId) {
                    this.radioValue = this.$options.ASSIGN_OWNER_TYPE.roundRobin;
                    this.generalUserOrRROptions = this.roundRobinListOptions;
                    this.userOrRoundRobinSelected = this.generalUserOrRROptions.find((u) => u.id === tempRoundRobinId);
                }
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.process.assign.owner.failed.load.init.config');

                this.$error({
                    message: errorMessage,
                });
            }
        },
    },
};
</script>

<style lang="scss">
.assign-owner-process-modal .radio {
    padding: $spacing-075 $spacing-100;
}

.assign-owner-process-modal .users-round-robin-lists {
    margin-top: $spacing-300;
    margin-bottom: $spacing-075;
}

.assign-owner-process-modal .checkbox-ignore-assigned-user {
    margin-top: $spacing-300;
    padding: 0;
}

.assign-owner-process-modal .multiselect-single label {
    padding: 0;
}
</style>
